import { useEffect, useRef, useState } from "react";
import { FullscreenWidget } from "@/components/ui/Fullscreen";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import type { PopupSurfaceState } from "@/surfaces/SurfaceTypes";
import { debugLog } from "@/utils";
import { useSubscription } from "@/hooks/useSubscription";

const OFFSCREEN_PARK_STYLE: React.CSSProperties = {
	position: "fixed",
	left: -100000,
	top: -100000,
	width: 1,
	height: 1,
	overflow: "hidden",
	pointerEvents: "none",
	opacity: 0,
};

const MODAL_LAYER_Z = 1;
const CLOSE_LAYER_Z = 2;

export type PopupSurfaceProps = {
	visible: boolean;
	fullscreenOpenIntentId?: number;
	onClose: () => void;
	children?: React.ReactNode;

	onSurfaceStateChange?: (state: PopupSurfaceState) => void;

	fullscreenBackgroundColor?: string;
	hasCloseBtn?: boolean;
};

function isSamePopupSurfaceState(
	current: PopupSurfaceState | null,
	next: PopupSurfaceState | null
): boolean {
	if (current === next) {
		return true;
	}

	if (!current || !next) {
		return false;
	}

	return (
		current.visible === next.visible &&
		current.enabled === next.enabled &&
		current.granted === next.granted &&
		current.parked === next.parked &&
		current.modalHost === next.modalHost &&
		current.parkingHost === next.parkingHost
	);
}

export function PopupSurface({
	visible,
	fullscreenOpenIntentId,
	onClose,
	children,
	onSurfaceStateChange,
	fullscreenBackgroundColor = "rgba(0, 0, 0, 0.92)",
	hasCloseBtn = true,
}: PopupSurfaceProps) {
	const {
		onFullscreenGranted,
		onExitFullscreenAck,
		exitFullscreen,
		onDisableAllFreeAreas,
		onEnableAllFreeAreas,
	} = useCommunicator();

	const [granted, setGranted] = useState(false);
	const [enabled, setEnabled] = useState(true);

	const exitingRef = useRef(false);
	const visibleRef = useRef(visible);
	const onCloseRef = useRef(onClose);
	const lastEmittedSurfaceStateRef = useRef<PopupSurfaceState | null>(null);

	const modalHostRef = useRef<HTMLDivElement | null>(null);
	const parkingHostRef = useRef<HTMLDivElement | null>(null);

	const instanceIdRef = useRef(
		`PopupSurface-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
	);

	useEffect(() => {
		debugLog("[PopupSurface] mounted", {
			instanceId: instanceIdRef.current,
			visible,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			fullscreenBackgroundColor,
			hasCloseBtn,
		});

		return () => {
			debugLog("[PopupSurface] unmounted", {
				instanceId: instanceIdRef.current,
				visible: visibleRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				granted,
				enabled,
				exiting: exitingRef.current,
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		visibleRef.current = visible;

		debugLog("[PopupSurface] visible prop changed", {
			instanceId: instanceIdRef.current,
			visible,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			granted,
			enabled,
			exiting: exitingRef.current,
		});
	}, [visible, fullscreenOpenIntentId, granted, enabled]);

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (visible) {
			debugLog("[PopupSurface] visible=true -> reset exitingRef", {
				instanceId: instanceIdRef.current,
				granted,
				enabled,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				previousExiting: exitingRef.current,
			});

			exitingRef.current = false;
		}
	}, [visible, fullscreenOpenIntentId, granted, enabled]);

	useSubscription(
		onFullscreenGranted,
		() => {
			debugLog("[PopupSurface] host -> FullscreenGranted", {
				instanceId: instanceIdRef.current,
				beforeGranted: granted,
				visible: visibleRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				enabled,
				exiting: exitingRef.current,
				modalHostPresent: !!modalHostRef.current,
				parkingHostPresent: !!parkingHostRef.current,
			});

			setGranted(true);
			exitingRef.current = false;
		},
		[granted, enabled, fullscreenOpenIntentId]
	);

	useSubscription(
		onExitFullscreenAck,
		() => {
			debugLog("[PopupSurface] host -> ExitFullscreenAck", {
				instanceId: instanceIdRef.current,
				beforeGranted: granted,
				visible: visibleRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				enabled,
				exiting: exitingRef.current,
				modalHostPresent: !!modalHostRef.current,
				parkingHostPresent: !!parkingHostRef.current,
			});

			setGranted(false);
			exitingRef.current = false;

			if (visibleRef.current) {
				debugLog("[PopupSurface] fullscreen exited while visible=true -> onClose()", {
					instanceId: instanceIdRef.current,
					fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				});
				onCloseRef.current();
			}
		},
		[granted, enabled, fullscreenOpenIntentId]
	);

	useSubscription(
		onDisableAllFreeAreas,
		() => {
			debugLog("[PopupSurface] host -> DisableAllFreeAreas", {
				instanceId: instanceIdRef.current,
				visible: visibleRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				granted,
				beforeEnabled: enabled,
			});
			setEnabled(false);
		},
		[granted, enabled, fullscreenOpenIntentId]
	);

	useSubscription(
		onEnableAllFreeAreas,
		() => {
			debugLog("[PopupSurface] host -> EnableAllFreeAreas", {
				instanceId: instanceIdRef.current,
				visible: visibleRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				granted,
				beforeEnabled: enabled,
			});
			setEnabled(true);
		},
		[granted, enabled, fullscreenOpenIntentId]
	);

	useEffect(() => {
		const modalHost = modalHostRef.current;
		const parkingHost = parkingHostRef.current;

		const parked = !granted;

		debugLog("[PopupSurface] state snapshot", {
			instanceId: instanceIdRef.current,
			visible,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			enabled,
			granted,
			parked,
			exiting: exitingRef.current,
			modalHostPresent: !!modalHost,
			parkingHostPresent: !!parkingHost,
			hasCloseBtn,
			fullscreenBackgroundColor,
		});

		const nextSurfaceState: PopupSurfaceState = {
			visible,
			enabled,
			granted,
			parked,
			modalHost,
			parkingHost,
		};

		const previousSurfaceState = lastEmittedSurfaceStateRef.current;

		if (isSamePopupSurfaceState(previousSurfaceState, nextSurfaceState)) {
			debugLog("[PopupSurface] onSurfaceStateChange skipped because popup state did not change", {
				instanceId: instanceIdRef.current,
				previousSurfaceState,
				nextSurfaceState,
			});
			return;
		}

		debugLog("[PopupSurface] onSurfaceStateChange emitting new popup state", {
			instanceId: instanceIdRef.current,
			previousSurfaceState,
			nextSurfaceState,
			changed: {
				visible: previousSurfaceState?.visible !== nextSurfaceState.visible,
				enabled: previousSurfaceState?.enabled !== nextSurfaceState.enabled,
				granted: previousSurfaceState?.granted !== nextSurfaceState.granted,
				parked: previousSurfaceState?.parked !== nextSurfaceState.parked,
				modalHost:
					previousSurfaceState?.modalHost !== nextSurfaceState.modalHost,
				parkingHost:
					previousSurfaceState?.parkingHost !== nextSurfaceState.parkingHost,
			},
		});

		lastEmittedSurfaceStateRef.current = nextSurfaceState;
		onSurfaceStateChange?.(nextSurfaceState);
	}, [
		enabled,
		granted,
		hasCloseBtn,
		fullscreenBackgroundColor,
		fullscreenOpenIntentId,
		onSurfaceStateChange,
		visible,
	]);

	useEffect(() => {
		if (visible) {
			debugLog("[PopupSurface] exit effect skipped because visible=true", {
				instanceId: instanceIdRef.current,
				granted,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				exiting: exitingRef.current,
			});
			return;
		}

		if (!granted) {
			debugLog("[PopupSurface] exit effect skipped because granted=false", {
				instanceId: instanceIdRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				exiting: exitingRef.current,
			});
			return;
		}

		if (exitingRef.current) {
			debugLog("[PopupSurface] exit effect skipped because exit already in flight", {
				instanceId: instanceIdRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
				granted,
			});
			return;
		}

		exitingRef.current = true;

		debugLog("[PopupSurface] visible=false while granted=true -> exitFullscreen()", {
			instanceId: instanceIdRef.current,
			visible,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			granted,
			enabled,
		});

		exitFullscreen();
	}, [visible, fullscreenOpenIntentId, granted, exitFullscreen, enabled]);

	useEffect(() => {
		if (!visible || granted) {
			return;
		}

		debugLog("[PopupSurface] waiting for FullscreenGranted", {
			instanceId: instanceIdRef.current,
			visible,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			granted,
			enabled,
			exiting: exitingRef.current,
			modalHostPresent: !!modalHostRef.current,
			parkingHostPresent: !!parkingHostRef.current,
		});
	}, [visible, fullscreenOpenIntentId, granted, enabled]);

	const close = () => {
		debugLog("[PopupSurface] close requested", {
			instanceId: instanceIdRef.current,
			visible: visibleRef.current,
			fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			granted,
			enabled,
			exiting: exitingRef.current,
		});

		onClose();

		if (granted && !exitingRef.current) {
			exitingRef.current = true;

			debugLog("[PopupSurface] close -> exitFullscreen()", {
				instanceId: instanceIdRef.current,
				fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
			});

			exitFullscreen();
		}
	};

	const shouldRenderFullscreen = visible || granted;

	debugLog("[PopupSurface] render decision", {
		instanceId: instanceIdRef.current,
		visible,
		fullscreenOpenIntentId: fullscreenOpenIntentId ?? null,
		granted,
		enabled,
		shouldRenderFullscreen,
	});

	return (
		<>
			<div ref={parkingHostRef} style={OFFSCREEN_PARK_STYLE} aria-hidden="true" />

			{shouldRenderFullscreen && (
				<FullscreenWidget
					backgroundColor={fullscreenBackgroundColor}
					autoRequest={visible}
					exitOnUnmount={false}
					openIntentId={fullscreenOpenIntentId}
					zIndex={10000}
				>
					{granted && (
						<div
							className="absolute inset-0"
							style={{ zIndex: MODAL_LAYER_Z, pointerEvents: "auto" }}
						>
							<div
								ref={modalHostRef}
								className="absolute inset-0"
								aria-label="CaptainUpModalHost"
							/>
						</div>
					)}

					{children}

					{hasCloseBtn && visible && (
						<div
							className="absolute right-4 top-4"
							style={{ zIndex: CLOSE_LAYER_Z, pointerEvents: "auto" }}
						>
							<button
								className="rounded-md bg-black/60 px-3 py-2 text-white"
								onClick={close}
							>
								Close
							</button>
						</div>
					)}
				</FullscreenWidget>
			)}
		</>
	);
}

export default PopupSurface;